
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";

export const HeroSection = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, language } = useLanguage();
  const { isAuthenticated, isCreator, isAdmin } = useAuth();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 400); // Show after scrolling past hero content
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine which buttons to show based on authentication status
  const renderAuthButtons = () => {
    if (isAuthenticated) {
      if (isAdmin) {
        // For admin users, show only Admin Dashboard button
        return (
          <Button asChild size="lg" className="h-12 px-8">
            <Link to="/admin">{t('sidebar.dashboard')}</Link>
          </Button>
        );
      } else {
        // For regular authenticated users, show dashboard and create buttons
        return (
          <>
            <Button asChild size="lg" className="h-12 px-8">
              <Link to="/creator/panel">{t('sidebar.dashboard')}</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-12 px-8">
              <Link to="/create-derivative">{t('sidebar.publishCreation')}</Link>
            </Button>
          </>
        );
      }
    } else {
      // For non-authenticated users, show get started and sign in buttons
      return (
        <>
          <Button asChild size="lg" className="h-12 px-8">
            <Link to="/auth/signup">{t('home.hero.cta')}</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-12 px-8">
            <Link to="/auth/login">{language === 'fr' ? 'Se connecter' : 'Login'}</Link>
          </Button>
        </>
      );
    }
  };

  // Determine which fixed buttons to show when scrolling
  const renderFixedButtons = () => {
    if (isAuthenticated) {
      if (isAdmin) {
        // For admin users, show only Admin Dashboard button when scrolled
        return (
          <Button asChild size="lg" className="h-10 px-6">
            <Link to="/admin">{t('sidebar.dashboard')}</Link>
          </Button>
        );
      } else {
        return (
          <>
            <Button asChild size="lg" className="h-10 px-6">
              <Link to="/creator/panel">{t('sidebar.dashboard')}</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-10 px-6">
              <Link to="/create-derivative">{t('sidebar.publishCreation')}</Link>
            </Button>
          </>
        );
      }
    } else {
      return (
        <>
          <Button asChild size="lg" className="h-10 px-6">
            <Link to="/auth/signup">{t('home.hero.cta')}</Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="h-10 px-6">
            <Link to="/auth/login">{language === 'fr' ? 'Se connecter' : 'Login'}</Link>
          </Button>
        </>
      );
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-primary/10 via-primary/5 to-transparent py-20 md:py-32">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-balance tracking-tight drop-shadow-[0_4px_20px_rgba(0,0,0,0.25)]">
            {t('home.hero.title')} <span className="text-primary">Re[ut</span>
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-10 text-balance max-w-2xl mx-auto drop-shadow-[0_4px_20px_rgba(0,0,0,0.25)]">
            {t('home.hero.subtitle')}
          </p>
          
          {/* Dynamic buttons in the hero section */}
          <div className="flex flex-wrap gap-4 justify-center">
            {renderAuthButtons()}
          </div>
        </div>
      </div>
      
      {/* Fixed buttons that appear when scrolling */}
      <div className={`fixed top-0 left-0 right-0 flex justify-center items-center gap-4 transition-all duration-500 z-50 py-3 bg-background/90 backdrop-blur-md shadow-md ${
        isScrolled ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
      }`}>
        {renderFixedButtons()}
      </div>
    </section>
  );
};
